import { domMax, m } from 'framer-motion';
import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import type { CapiPlp, GridSize } from 'types/capi/plp';
import type {
  ProductListItem,
  ProductListItemExternal,
  ProductListItemInternal
} from 'types/capi/shared';

import FramerMotion from '~/components/FramerMotion';
import ProductCard from '~/components/ProductCard';
import usePageData from '~/hooks/usePageData';
import useQsParams from '~/hooks/useQsParams';

import ImageTile from '../ImageTile';
import { MidModuleSection } from '../ModuleSection';
import Pagination from './Pagination';

const MODULE_TYPE = 'plp-main-list';

const gridStyles = {
  large: 'grid-cols-1 md:grid-cols-3 ',
  small: 'grid-cols-2 md:grid-cols-4'
};

type ImageTileConfig = {
  after: number;
  field:
    | 'image_1_section_modules'
    | 'image_2_section_modules'
    | 'image_3_section_modules';
  minProducts: number;
  placement: string;
  slot: 1 | 2 | 3;
};

// `after` places the tile on mobile, where it flows with the products. On desktop it
// spans two cards each way at a fixed cell, and the products flow around it, so its
// position in the list is irrelevant there. The two cannot be expressed as one order.
const imageTiles: Record<GridSize, ImageTileConfig[]> = {
  large: [
    {
      after: 2,
      field: 'image_1_section_modules',
      minProducts: 2,
      placement: 'md:col-start-2 md:row-start-[1]',
      slot: 1
    },
    {
      after: 16,
      field: 'image_2_section_modules',
      minProducts: 19,
      placement: 'md:col-start-1 md:row-start-[9]',
      slot: 2
    },
    {
      after: 30,
      field: 'image_3_section_modules',
      minProducts: 36,
      placement: 'md:col-start-2 md:row-start-[15]',
      slot: 3
    }
  ],
  small: [
    {
      after: 4,
      field: 'image_1_section_modules',
      minProducts: 4,
      placement: 'md:col-start-3 md:row-start-[1]',
      slot: 1
    },
    {
      after: 16,
      field: 'image_2_section_modules',
      minProducts: 20,
      placement: 'md:col-start-1 md:row-start-[7]',
      slot: 2
    },
    {
      after: 30,
      field: 'image_3_section_modules',
      minProducts: 36,
      placement: 'md:col-start-3 md:row-start-[12]',
      slot: 3
    }
  ]
};

const imageTileStyles =
  'col-span-full -mx-4 aspect-[4/5] md:mx-0 md:aspect-auto md:col-span-2 md:row-span-2 md:h-full';

const Products = ({
  gridSize = 'small',
  allowNotifications,
  listId,
  modulePosition
}: {
  allowNotifications: boolean;
  gridSize: GridSize;
  listId: string;
  modulePosition: number;
}) => {
  const { q } = useQsParams();
  const plp = usePageData<CapiPlp>();
  const pageType = q ? 'search' : 'plp';

  const tiles = imageTiles[gridSize].filter(
    (tile) =>
      !!plp[tile.field]?.length && plp.products.length >= tile.minProducts
  );

  // On the large grid the first tile leaves only one product per row above it,
  // so the twelfth product lands mid-row and the full-width mid banner would
  // strand two empty cards beside it. Pinning the banner to the row after the
  // eleventh keeps the rows whole.
  const midPlacement =
    gridSize === 'large' && tiles.some((tile) => tile.slot === 1)
      ? 'md:row-start-[6]'
      : '';

  const gridChildren: ReactNode[] = [];

  plp.products.forEach((product, index) => {
    if (index === 12)
      gridChildren.push(
        <MidModuleSection className={midPlacement} key="plp-mid" />
      );

    tiles
      .filter((tile) => tile.after === index)
      .forEach((tile) =>
        gridChildren.push(
          <ImageTile
            className={`${imageTileStyles} ${tile.placement}`}
            key={`plp-image-${tile.slot}`}
            modules={plp[tile.field]}
            slot={tile.slot}
          />
        )
      );

    gridChildren.push(
      <FramerMotion feature={domMax} key={`plp-product-${index}`}>
        <m.div layout>
          {product.type === 'external' ? (
            <ProductCardExternal
              allowNotifications={allowNotifications}
              listId={listId}
              modulePosition={modulePosition}
              pageType={pageType}
              position={index + 1}
              product={product as ProductListItemExternal}
            />
          ) : (
            <ProductCardInternal
              allowNotifications={allowNotifications}
              gridSize={gridSize}
              listId={listId}
              modulePosition={modulePosition}
              pageType={pageType}
              position={index + 1}
              product={product as ProductListItemInternal}
            />
          )}
        </m.div>
      </FramerMotion>
    );
  });

  return (
    <div className={!q ? 'min-h-screen py-6 desktop:w-desktop' : 'py-6'}>
      {plp.products.length > 0 ? (
        <div className="flex flex-col gap-4">
          <div
            className={`grid gap-x-2 gap-y-4 md:gap-6 ${gridStyles[gridSize]}`}
          >
            {gridChildren}
          </div>
          <div className="mt-6">
            <Pagination pagination={plp.pagination} />
          </div>
        </div>
      ) : (
        <NoResultsMessage />
      )}
    </div>
  );
};

function ProductCardInternal({
  product,
  allowNotifications,
  gridSize,
  listId,
  modulePosition,
  position,
  pageType
}: {
  allowNotifications: boolean;
  gridSize: GridSize;
  listId: string;
  modulePosition: number;
  pageType: 'plp' | 'search';
  position: number;
  product: ProductListItemInternal;
}) {
  function onProductCardClick(product: ProductListItem) {
    if (product.queryID) localStorage.setItem('queryID', product.queryID);
  }

  return (
    <ProductCard.Internal
      allowNotifications={allowNotifications}
      cardSize={gridSize}
      listId={listId}
      modulePosition={modulePosition}
      moduleType={MODULE_TYPE}
      position={position}
      padImage={true}
      product={product}
      imageLoading={position <= 10 ? 'eager' : 'lazy'}
      onClick={() => onProductCardClick(product)}
      pageType={pageType}
      pagePosition="PLP Main List"
    />
  );
}

function ProductCardExternal({
  product,
  allowNotifications,
  listId,
  modulePosition,
  position,
  pageType
}: {
  allowNotifications: boolean;
  listId: string;
  modulePosition: number;
  pageType: 'plp' | 'search';
  position: number;
  product: ProductListItemExternal;
}) {
  return (
    <ProductCard.External
      product={product}
      allowNotifications={allowNotifications}
      listId={listId}
      modulePosition={modulePosition}
      moduleType={MODULE_TYPE}
      position={position}
      padImage
      className="h-auto min-w-40"
      imageLoading="eager"
      cardSize="small"
      pagePosition="PLP Quick Buy"
      overrideInteractionPagePosition={true}
      pageType={pageType}
    />
  );
}

const NoResultsMessage = () => {
  const { t } = useTranslation('plp');

  return (
    <h2 className="text-caption-grey flex items-center justify-center py-6 text-center">
      {t('noResults')}
    </h2>
  );
};

export default Products;
